/**
 * A drawing the admin picked, before it goes anywhere: the browser checks
 * only what saves a pointless upload (PNG, at most 5 MB) and reads the size
 * from the PNG header with core's inspectPng — the same reader the server
 * uses, so the preview shows the proportions the pins will be measured
 * against. The server checks again and decides (docs/fleet-admin-ui.md 5.2).
 */
import { inspectPng, MAX_DRAWING_BYTES } from '@fsv/core';
import { tx } from '@fsv/data';

export type PickedDrawing =
  | { ok: true; file: File; width: number; height: number; alpha: boolean; previewUrl: string }
  | { ok: false; message: string };

export async function readDrawingFile(file: File): Promise<PickedDrawing> {
  const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);
  if (!isPng) return { ok: false, message: tx.admin.fleet.notPngLocal };
  if (file.size > MAX_DRAWING_BYTES) return { ok: false, message: tx.admin.fleet.tooLargeLocal };
  // The whole file (≤ 5 MB): inspectPng also wants IEND at the end.
  const verdict = inspectPng(new Uint8Array(await file.arrayBuffer()));
  if (!verdict.ok) return { ok: false, message: tx.admin.fleet.notPngLocal };
  return { ok: true, file, width: verdict.width, height: verdict.height, alpha: verdict.alpha, previewUrl: URL.createObjectURL(file) };
}

/**
 * The demo store's copy of an uploaded drawing: scaled so the longer edge is
 * at most `maxEdge`, as PNG (transparency kept). localStorage holds a few MB
 * per origin; a 4000-px original would not fit twice. Width and height of the
 * original stay what the pins are measured against.
 */
export async function downscalePng(blob: Blob, maxEdge = 800): Promise<string | null> {
  if (typeof document === 'undefined' || typeof createImageBitmap === 'undefined') return null;
  try {
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
